import { NavigateFunction } from 'react-router-dom'
import showResponseError from '../../../utils/showResponseError'
import { t } from 'i18next'
/* eslint-disable camelcase */
import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'

import { AppHttpConfig as API } from '../../../services/httpConfig'
import {
  City,
  IBasicInfoInitialState,
  Province,
  ProvinceCitiesResponce,
  ProvinceList,
  ProvinceResponse,
} from './types'
import { BasicInfoUrl } from './urls'
import toastify from '../../../utils/toastify'
import { ToastType } from '../../../types/generalType'
import queryStringBuilder, { QueryParams } from '../../../utils/queryStringBuilder'
import hasObjectData from '../../../utils/hasObjectData'

const initialState: IBasicInfoInitialState = {
  loading: false,
  province: {
    provinceList: {
      currentPage: 1,
      loading: false,
      dataList: {
        total: 0,
        data: [],
      },
    },
    ciyList: {
      loading: false,
      list: [],
    },
    provinceItem: {
      loading: false,
      itemData: null,
    },
    createProvince: {
      loading: false,
    },
    updateProvince: {
      loading: false,
    },
  },
}

// get all province
export const getAllProvince = createAsyncThunk<ProvinceResponse, QueryParams>(
  'basicinfo/province/getAllprovince',
  async (data: QueryParams) => {
    const query = hasObjectData(data) ? queryStringBuilder(data) : ''
    try {
      const response = await API.get(`${BasicInfoUrl.GetAllProvince}${query}`)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)

// get all Cities of Province with province city
export const getProvinceCities = createAsyncThunk<ProvinceCitiesResponce, QueryParams>(
  'basicinfo/province/getProvinceCities',
  async (data: QueryParams) => {
    const query = hasObjectData(data) ? queryStringBuilder(data) : ''
    try {
      const response = await API.get(`${BasicInfoUrl.GetProviceCities}${query}`)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)
// get province by id
export const getProvinceById = createAsyncThunk<ProvinceList, string>(
  'basicinfo/province/getProvinceById',
  async (id: string) => {
    try {
      const response = await API.get(`${BasicInfoUrl.GetProvinceById}?id=${id}`)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)
// crate proclamation
export const createProvince = createAsyncThunk<
  number,
  { data: Province; navigate: NavigateFunction }
>('basicinfo/province/createProvince', async (command) => {
  const { data, navigate } = command
  try {
    const response = await API.post(BasicInfoUrl.CreateProvince, data)
      .then((res) => {
        if (res.status === 201) {
          navigate('/BasicInfo/Province')
          toastify(ToastType.SUCCESS, { operatin: t('create'), pageName: t('province') })
          return res.data
        }
      })
      .catch((error) => {
        showResponseError(error)
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

export const updateProvince = createAsyncThunk<
  any,
  { data: ProvinceList; navigate: NavigateFunction }
>('basicinfo/province/updateProvince', async (command) => {
  const { data, navigate } = command

  try {
    const response = await API.put(BasicInfoUrl.UpdateProvince, data)
      .then((res) => {
        if (res.status === 201) {
          toastify(ToastType.SUCCESS, { operatin: t('edit'), pageName: t('province') })
          navigate('/BasicInfo/Province')
          return res.data
        }
      })
      .catch((error) => {
        showResponseError(error)
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

const proclamationSlice = createSlice({
  name: 'basicInfo',
  initialState,
  reducers: {
    clearProvinceList: (state) => {
      state.province.provinceList.dataList = { total: 0, data: [] }
      state.province.provinceList.currentPage = 1
    },
    setProviceListCurrentPage: (state, action: PayloadAction<number>) => {
      state.province.provinceList.currentPage = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IBasicInfoInitialState>) => {
    // get all province
    builder
      .addCase(getAllProvince.pending, (state) => {
        state.province.provinceList.loading = true
      })
      .addCase(getAllProvince.fulfilled, (state, action: PayloadAction<ProvinceResponse, any>) => {
        state.province.provinceList.loading = false
        state.province.provinceList.dataList = action.payload
      })
      .addCase(getAllProvince.rejected, (state) => {
        state.province.provinceList.loading = false
        state.province.provinceList.dataList = { total: 0, data: [] }
      })
    // get province cities
    builder
      .addCase(getProvinceCities.pending, (state) => {
        state.province.ciyList.loading = true
      })
      .addCase(
        getProvinceCities.fulfilled,
        (state, action: PayloadAction<ProvinceCitiesResponce, any>) => {
          state.province.ciyList.loading = false
          state.province.ciyList.list = action.payload
        },
      )
      .addCase(getProvinceCities.rejected, (state) => {
        state.province.ciyList.loading = false
        state.province.ciyList.list = []
      })

    // get province by id
    builder
      .addCase(getProvinceById.pending, (state) => {
        state.province.provinceItem.loading = true
      })
      .addCase(getProvinceById.fulfilled, (state, action: PayloadAction<ProvinceList, any>) => {
        state.province.provinceItem.loading = false
        state.province.provinceItem.itemData = action.payload
      })
      .addCase(getProvinceById.rejected, (state) => {
        state.province.provinceItem.loading = false
        state.province.provinceItem.itemData = null
      })

    // create province
    builder
      .addCase(createProvince.pending, (state) => {
        state.province.createProvince.loading = true
      })
      .addCase(createProvince.fulfilled, (state) => {
        state.province.createProvince.loading = false
      })
      .addCase(createProvince.rejected, (state) => {
        state.province.createProvince.loading = false
      })
    // update province
    builder
      .addCase(updateProvince.pending, (state) => {
        state.province.updateProvince.loading = true
      })
      .addCase(updateProvince.fulfilled, (state) => {
        state.province.updateProvince.loading = false
      })
      .addCase(updateProvince.rejected, (state) => {
        state.province.updateProvince.loading = false
      })
  },
})

export const { clearProvinceList, setProviceListCurrentPage } = proclamationSlice.actions

export default proclamationSlice.reducer
